import { Injectable } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { Organization, User, UserToken } from '../models/user';
import { Media } from '../models/media';
import { BillingDetails, Invoice, Plan } from '../models/billing';
import { BehaviorSubject, firstValueFrom, timeout } from 'rxjs';
import { ConstantsService } from '../services/constants.service';
import { UtilsService } from '../services/utils.service';
import { Pagination } from '../models/pagination';
import { SeoLanguage, SeoLanguageFlat } from '../models/dataforseo/language';
import { Router, RouterLink } from '@angular/router';
import { CurrentProject } from 'src/app/pages/settings/models/current-project';
import { Project } from 'src/app/pages/settings/models/project';
import { CoreService } from '../http-services/core.service';
import { environment } from 'src/environments/environment';
import { SeoLimits } from 'src/app/pages/seo/models/seo-limits';
import { Location } from '@angular/common';
import { BillingProduct } from 'src/app/pages/billing/models/billing-product';
import { BillingService } from 'src/app/pages/billing/billing.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Injector } from '@angular/core';
import { OrganizationSettingsService } from 'src/app/pages/organization-settings/services/organization-settings.service';
import { PermissionMatrixStore } from '../services/permission-matrix.store';
import { PermissionsService } from '../services/permissions.service';


declare var Stripe;


@Injectable({
  providedIn: 'root'
})
export class InMemoryCacheService {
  private _token: UserToken;
  private _media: Media[];
  private _billingDetails: BillingDetails;
  private _invoice: Invoice[];
  private _plans: Plan[];
  private _selectedPlan: Plan;

  private _showErrorModal: boolean;
  private _payBtnDisabled: boolean;
  private _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private _stripe: any;
  private _pagination: Pagination;
  private _seoLanguagesFlat: SeoLanguageFlat[];
  private _seoLanguages: SeoLanguage[];
  private _profile: any;
  private _amazonMerchantLocations: any;
  private _amazonMerchantLanguages: any;
  private _tripAdvisorLocations: any;
  private _tripAdvisorLanguages: any;
  private _claims: any = [];
  private _mainMenu: any[] = [];
  private _mainMenuChanged: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private _menuMode: any;
  private _menuModeChanged: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private _seoLimits: SeoLimits;
  private _selectedProject: any;
  private _loaderCount: number = 0;
  private _billingProducts: BillingProduct[];
  private _accessToken: string;
  private _propertyId: string;

  constructor(private sanitizer: DomSanitizer, private authService: AuthService, private constantService: ConstantsService, public utils: UtilsService,
    private router: Router, private core: CoreService<{}>, private location: Location, private billingService: BillingService, private injector: Injector) {
    this.loading = false;
  }


  public set page(value: string) {

  }

  public clearData() {
    this.utils.logger.info(`InMemoryCacheService.clearData()`);
    localStorage.removeItem('currentUser');
    this.clearPermissionMatrixCache();
    this._token = null;
    this._billingDetails = null;
    this._claims = null;
    this._pagination = null;
    this._profile = null;
    this._selectedPlan = null;
    this._plans = null;
    this._stripe = null;
    this._seoLimits = null;
    this._selectedProject = null;
    this._amazonMerchantLocations = null;
    this._amazonMerchantLanguages = null;
    this._seoLanguages = null;
    this._seoLanguagesFlat = null;
    this._media = null;
    this._invoice = null;
    this._billingDetails = null;
    this._billingProducts = null;
  }

  public logout(gotoLogin: boolean = true) {
    this.utils.logger.info(`InMemoryCacheService.logout()`);
    this.clearData();
    this.clearOrganizationContext();
    if (gotoLogin) {
      this.router.navigate(['/account/login']);
    }
    this.getMainMenu();
    this.updateMenuMode = 'horizontal';
  }

  public stripeInit() {
    this.utils.logger.info(`InMemoryCacheService.stripeInit()`);
    if (!this._stripe) {
      this.utils.logger.info(`InMemoryCacheService.stripeInit() - setting stripe`);
      const token = this.utils.parseJwt(this.auth?.token);
      this._stripe = Stripe(token.billingKey); // use your test publishable key
    }
  }

  public setSeoLanguagesFlat(languageCodes: any[]) {
    this.utils.logger.info(`InMemoryCacheService.setSeoLanguagesFlat()`);
    this._seoLanguagesFlat = this.utils.filterSeoLanguagesFlat(this._seoLanguages, languageCodes);
  }

  public async setSeoLanguages() {
    this.utils.logger.info(`InMemoryCacheService.setSeoLanguages()`);
    const { data, error } = await this.getLocationsAndLanguages();
    if (data) {
      this.utils.logger.info(`InMemoryCacheService.setSeoLanguages() - got data from getLocationsAndLanguages`);
      this._seoLanguages = data;
    }
  }

  public deleteCurrentProject() {
    this.utils.logger.info(`InMemoryCacheService.deleteCurrentProject()`);
    localStorage.removeItem('currentProject');
    this._selectedProject = null;
  }

  public async getLocationsAndLanguages(handleError: boolean = false) {
    this.utils.logger.info(`InMemoryCacheService.getLocationsAndLanguages()`);

    if (!this.userId) {
      this.utils.logger.info(`InMemoryCacheService.getLocationsAndLanguages() - no userId, returning null`);
      return null;
    }

    const response = { data: null, error: null };

    try {
      // Check in-memory cache
      if (this._seoLanguages) {
        this.utils.logger.info(`InMemoryCacheService.getLocationsAndLanguages() - found data in memory`);
        response.data = this._seoLanguages;
        return response;
      }

      // Check local storage first
      const cachedData = localStorage.getItem('seoLanguages');
      if (cachedData) {
        this.utils.logger.info(`InMemoryCacheService.getLocationsAndLanguages() - found data in local storage`);
        this._seoLanguages = JSON.parse(cachedData);
        response.data = this._seoLanguages;
        return response;
      }

      // Fetch from API
      this.utils.logger.info(`InMemoryCacheService.getLocationsAndLanguages() - fetching data from API`);
      // SEO language data is optional during sign-in. Do not let an unresponsive
      // SEO service keep the login screen's loading overlay up indefinitely.
      this._seoLanguages = await firstValueFrom(
        this.core.get(`${environment.seoUrl}/seosettings/locationsAndLanguages`)
          .pipe(timeout({ first: 8000 }))
      ) as SeoLanguage[];
      localStorage.setItem('seoLanguages', JSON.stringify(this._seoLanguages));
      response.data = this._seoLanguages;
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async getAmazonMerchantLocations(handleError: boolean = false) {
    this.utils.logger.info(`InMemoryCacheService.getAmazonMerchantLocations()`);

    const response = { data: null, error: null };

    try {
      // Check in-memory cache
      if (this._amazonMerchantLocations) {
        this.utils.logger.info(`InMemoryCacheService.getAmazonMerchantLocations() - found data in memory`);
        response.data = this._amazonMerchantLocations;
        return response;
      }

      // Check local storage first
      const cachedData = localStorage.getItem('amazonMerchantLocations');
      if (cachedData) {
        this.utils.logger.info(`InMemoryCacheService.getAmazonMerchantLocations() - found data in local storage`);
        this._amazonMerchantLocations = JSON.parse(cachedData);
        response.data = this._amazonMerchantLocations;
        return response;
      }

      // Fetch from API
      this.utils.logger.info(`InMemoryCacheService.getAmazonMerchantLocations() - fetching data from API`);
      this._amazonMerchantLocations = await firstValueFrom(this.core.get(`${environment.seoUrl}/seosettings/amazon/merchant/locations`));
      localStorage.setItem('amazonMerchantLocations', JSON.stringify(this._amazonMerchantLocations));
      response.data = this._amazonMerchantLocations;
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async getAmazonMerchantLanguages(handleError: boolean = false) {
    this.utils.logger.info(`InMemoryCacheService.getAmazonMerchantLanguages()`);

    const response = { data: null, error: null };

    try {
      // Check in-memory cache
      if (this._amazonMerchantLanguages) {
        this.utils.logger.info(`InMemoryCacheService.getAmazonMerchantLanguages() - found data in memory`);
        response.data = this._amazonMerchantLanguages;
        return response;
      }

      // Check local storage first
      const cachedData = localStorage.getItem('amazonMerchantLanguages');
      if (cachedData) {
        this.utils.logger.info(`InMemoryCacheService.getAmazonMerchantLanguages() - found data in local storage`);
        this._amazonMerchantLanguages = JSON.parse(cachedData);
        response.data = this._amazonMerchantLanguages;
        return response;
      }

      // Fetch from API
      this.utils.logger.info(`InMemoryCacheService.getAmazonMerchantLanguages() - fetching data from API`);
      this._amazonMerchantLanguages = await firstValueFrom(this.core.get(`${environment.seoUrl}/seosettings/amazon/merchant/languages`));
      localStorage.setItem('amazonMerchantLanguages', JSON.stringify(this._amazonMerchantLanguages));
      response.data = this._amazonMerchantLanguages;
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  // Helper: Check if IndexedDB is available
  private isIndexedDBAvailable(): boolean {
    return 'indexedDB' in window;
  }

  // Helper: Get from IndexedDB
  private getFromIndexedDB<T>(storeName: string, key: string): Promise<T | null> {
    return new Promise((resolve, reject) => {
      if (!this.isIndexedDBAvailable()) return resolve(null);
      const request = indexedDB.open('InMemoryCacheDB', 1);
      request.onupgradeneeded = function (event: any) {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName);
        }
      };
      request.onsuccess = function (event: any) {
        const db = event.target.result;
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const getRequest = store.get(key);
        getRequest.onsuccess = function () {
          resolve(getRequest.result || null);
        };
        getRequest.onerror = function () {
          resolve(null);
        };
      };
      request.onerror = function () {
        resolve(null);
      };
    });
  }

  // Helper: Set to IndexedDB
  private setToIndexedDB<T>(storeName: string, key: string, value: T): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isIndexedDBAvailable()) return resolve();
      const request = indexedDB.open('InMemoryCacheDB', 1);
      request.onupgradeneeded = function (event: any) {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName);
        }
      };
      request.onsuccess = function (event: any) {
        const db = event.target.result;
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        store.put(value, key);
        transaction.oncomplete = function () {
          resolve();
        };
        transaction.onerror = function () {
          resolve();
        };
      };
      request.onerror = function () {
        resolve();
      };
    });
  }

  public async getTripAdvisorLocations(handleError: boolean = false) {
    this.utils.logger.info(`InMemoryCacheService.getTripAdvisorLocations()`);

    const response = { data: null, error: null };

    try {
      // Check in-memory cache
      if (this._tripAdvisorLocations) {
        this.utils.logger.info(`InMemoryCacheService.getTripAdvisorLocations() - found data in memory`);
        response.data = this._tripAdvisorLocations;
        return response;
      }

      // Check IndexedDB
      const indexedDbData = await this.getFromIndexedDB<any>('tripAdvisor', 'locations');
      if (indexedDbData) {
        this.utils.logger.info(`InMemoryCacheService.getTripAdvisorLocations() - found data in IndexedDB`);
        this._tripAdvisorLocations = indexedDbData;
        response.data = this._tripAdvisorLocations;
        return response;
      }

      // Fetch from API
      this.utils.logger.info(`InMemoryCacheService.getTripAdvisorLocations() - fetching data from API`);
      this._tripAdvisorLocations = await firstValueFrom(this.core.get(`${environment.seoUrl}/seosettings/tripadvisor/locations`));
      // Store in both IndexedDB and localStorage
      await this.setToIndexedDB('tripAdvisor', 'locations', this._tripAdvisorLocations);
      response.data = this._tripAdvisorLocations;
    } catch (error) {
      response.error = error;
    }

    return response;
  }
  public async getTripAdvisorLanguages(handleError: boolean = false) {
    this.utils.logger.info(`InMemoryCacheService.getTripAdvisorLanguages()`);
    const response = { data: null, error: null };

    try {
      // Check in-memory cache
      if (this._tripAdvisorLanguages) {
        this.utils.logger.info(`InMemoryCacheService.getTripAdvisorLanguages() - found data in memory`);
        response.data = this._tripAdvisorLanguages;
        return response;
      }
      // Check local storage first
      const cachedData = localStorage.getItem('tripAdvisorLanguages');
      if (cachedData) {
        this.utils.logger.info(`InMemoryCacheService.getTripAdvisorLanguages() - found data in local storage`);
        this._tripAdvisorLanguages = JSON.parse(cachedData);
        response.data = this._tripAdvisorLanguages;
        return response;
      }

      // Fetch from API
      this.utils.logger.info(`InMemoryCacheService.getTripAdvisorLanguages() - fetching data from API`);
      this._tripAdvisorLanguages = await firstValueFrom(this.core.get(`${environment.seoUrl}/seosettings/tripadvisor/languages`));
      localStorage.setItem('tripAdvisorLanguages', JSON.stringify(this._tripAdvisorLanguages));
      response.data = this._tripAdvisorLanguages;
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async getSeoLimits(handleError: boolean = false): Promise<any> {
    this.utils.logger.info(`InMemoryCacheService.getSeoLimits()`);
    const response = { data: null, error: null };

    try {
      if (!this._seoLimits) {
        this.utils.logger.info(`InMemoryCacheService.getSeoLimits() - getting limits from server`);
        response.data = this._seoLimits = await firstValueFrom(this.core.get(`${environment.seoUrl}/seosettings/limits`));
      }

    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async getBillingProducts(handleError: boolean = false): Promise<any> {
    this.utils.logger.info(`ProfileComponent.ngOnInit() - get list of products associated with user`);

    if (this._billingProducts && this._billingProducts.length > 0) {
      this.utils.logger.info(`ProfileComponent.ngOnInit() - found products in memory`);
      return this._billingProducts;
    }

    this.loading = true;
    const { data, error } = await this.billingService.getUserBillingProducts(this.auth.organization.stripeCustomerId);
    this.loading = false;
    if (error) {
      this.utils.logger.error(`ProfileComponent.ngOnInit() - Error getting user billing products`, error);
      return false;
    }

    this._billingProducts = data;
    return true;
  }

  public async getBusinessCardPlanName(): Promise<string> {
    if (!this._billingProducts?.length) {
      await this.getBillingProducts();
    }

    return this._billingProducts?.find((product: BillingProduct) => product?.feature === 'businessCard')?.name ?? '';
  }

  public async getClaims(handleError: boolean = false): Promise<any> {
    this.utils.logger.info(`InMemoryCacheService.getClaims()`);

    if (!this.auth.organization) {
      this.utils.logger.info(`InMemoryCacheService.getClaims() - no userId, returning null`);
      return null;
    }

    const response = { data: null, error: null };

    try {
      // Check in-memory cache
      // if (this._claims && this._claims.length > 0) {
      //   this.utils.logger.info(`InMemoryCacheService.getClaims() - found data in memory`);
      //   response.data = this._claims;
      //   return response;
      // }

      // Fetch from API
      this.utils.logger.info(`InMemoryCacheService.getClaims() - fetching data from API`);
      response.data = this._claims = await this.core.get(`${environment.identityUrl}/OrganizationClaim/organization/${this.user.currentOrganizationId}`).toPromise();
    } catch (error) {
      response.error = error;
    }

    this.utils.logger.info(`InMemoryCacheService.getClaims() - got claims`, response);
    return response;
  }

  public getPlanClaims(): string[] {
    const normalizedPlans: string[] = (this._claims ?? [])
      .filter((claim: { type?: string; value?: string }) => claim?.type?.toLowerCase() === 'plan' && !!claim?.value)
      .map((claim: { value?: string }) => claim.value?.trim() ?? '')
      .filter((value: string) => !!value);

    return [...new Set(normalizedPlans)];
  }

  public async getGoogleAnalyticsData(data: any, handleError: boolean = false): Promise<any> {
    this.utils.logger.info(`InMemoryCacheService.getGoogleAccessRefreshTokens()`);

    const params = data;

    const url = `${environment.identityUrl}/googleAnalytics`;

    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${url}`, params));

      if (response?.data?.accessToken) {
        this.selectedProject.externalProviders.google.accessToken = response?.data?.accessToken;
      }
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async getGoogleAnalyticsBatchData(data: unknown): Promise<{ data: any; error: unknown }> {
    const response: { data: any; error: unknown } = { data: null, error: null };
    try {
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/googleAnalytics/batch`, data));
    } catch (error) {
      response.error = error;
    }
    return response;
  }

  public async getSearchConsoleData(data: any, handleError: boolean = false): Promise<any> {
    this.utils.logger.info(`InMemoryCacheService.getSearchConsoleData()`);

    const response = { data: null, error: null };

    try {
      const dimensions = Array.isArray(data?.dimensions) ? data.dimensions : [];
      const dimensionQuery = dimensions
        .filter((dimension: string) => !!dimension)
        .map((dimension: string) => `dimensions=${encodeURIComponent(dimension)}`)
        .join('&');

      const queryParts = [
        `projectId=${encodeURIComponent(data?.projectId ?? '')}`,
        `siteUrl=${encodeURIComponent(data?.siteUrl ?? '')}`,
        `startDate=${encodeURIComponent(data?.startDate ?? '')}`,
        `endDate=${encodeURIComponent(data?.endDate ?? '')}`,
        dimensionQuery
      ].filter((part: string) => !!part);

      const url = `${environment.crawlerServiceUrl}/SearchConsole/analytics?${queryParts.join('&')}`;
      response.data = await firstValueFrom(this.core.get(url));
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async createGoogleOAuthState(projectId: string, propertyId: string, siteUrl: string = ''): Promise<any> {
    const response = { data: null, error: null };
    try {
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/googleoauth/state`, { projectId, propertyId, siteUrl }));
    } catch (error) {
      response.error = error;
    }
    return response;
  }

  public async getGoogleAccessRefreshTokens(code: string, state: string, handleError: boolean = false): Promise<any> {
    this.utils.logger.info(`InMemoryCacheService.getGoogleAccessRefreshTokens()`);

    const params = {
      "code": code,
      "projectId": this.currentProject?.id,
      "userId": this.userId,
      "state": state
    };

    const url = `${environment.identityUrl}/googleoauth`;


    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${url}`, params));
    } catch (error) {
      response.error = error;
    }

    return response;
  }
  public async disconnectGoogleAccess(disconnectRequest: any, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/googleoauth/revoke`, disconnectRequest));
    } catch (error) {
      response.error = error;
    }

    return response;
  }
  public saveGoogleAccessToken(token: string): boolean {
    this.utils.logger.info(`InMemoryCacheService.saveGoogleAccessToken()`);
    if (!token) {
      this.utils.logger.info(`InMemoryCacheService.saveGoogleAccessToken() - no token, returning false`);
      return false;
    }

    this.utils.logger.info(`InMemoryCacheService.saveGoogleAccessToken() - saving token`);
    localStorage.setItem('googleAccessToken', token);
    this._accessToken = token;
    return true;
  }

  public saveGooglePropertyId(propertyId: string): boolean {
    this.utils.logger.info(`InMemoryCacheService.saveGooglePropertyId()`);
    if (!propertyId) {
      this.utils.logger.info(`InMemoryCacheService.saveGooglePropertyId() - no token, returning false`);
      return false;
    }

    this.utils.logger.info(`InMemoryCacheService.saveGooglePropertyId() - saving property id`);
    localStorage.setItem('googlePropertyId', propertyId);
    this._propertyId = propertyId;
    return true;
  }

  public getGoogleAccessToken(): string {
    this.utils.logger.info(`InMemoryCacheService.getGoogleAccessToken()`);
    return this?.selectedProject?.externalProviders?.google?.accessToken;
  }

  public deleteGoogleAccessToken(): boolean {
    this.utils.logger.info(`InMemoryCacheService.deleteGoogleAccessToken()`);
    localStorage.removeItem('googleAccessToken');
    this._accessToken = null;
    return true;
  }

  public deleteGooglePropertyId(): boolean {
    this.utils.logger.info(`InMemoryCacheService.deleteGooglePropertyId()`);
    localStorage.removeItem('googlePropertyId');
    this._propertyId = null;
    return true;
  }


  public getClaimByType(type: string, value: string): any {
    //this.utils.logger.info(`InMemoryCacheService.getClaimByType() type ${type}`);

    if (!type) {
     // this.utils.logger.info(`InMemoryCacheService.getClaimByType() - no type, returning null`);
      return null;
    }

    let claim = null;
    if (this._claims && this._claims.length > 0) {
      claim = this._claims?.find(c => c.type.toLowerCase() === type.toLowerCase() && c.value.toLowerCase() === value.toLowerCase());
    }
    //this.utils.logger.info(`InMemoryCacheService.getClaimByType() type ${type} - ${JSON.stringify(this._claims)}`)
    return claim;
  }

  public getIcon(path: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(`<img src="${path}" width="16" height="16">`);
  }

  private clearOrganizationContext(): void {
    const orgService = this.injector.get(OrganizationSettingsService, null);
    if (orgService && typeof orgService.clearOrganizationState === 'function') {
      orgService.clearOrganizationState();
    }
  }

  private clearPermissionMatrixCache(): void {
    const permissionMatrixStore = this.injector.get(PermissionMatrixStore, null);
    if (permissionMatrixStore && typeof permissionMatrixStore.clearPersistedCache === 'function') {
      permissionMatrixStore.clearPersistedCache();
    }
  }

  getParentIconSVG(path: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(`<img src="${path}" width="30" height="30">`);
  }

  public async getMainMenu(): Promise<void> {
    this.utils.logger.info(`InMemoryCacheService.getMainMenu()`);
    this._mainMenu = [];

    if (this.location.path().split('?')[0].includes('account') || !this.user) {
      this.utils.logger.info(`InMemoryCacheService.getMainMenu() - account flow`);
      // this._mainMenu.push(
      //   { label: 'Home', url: 'https://insightgainer.com/' },
      //   { label: 'Pricing', url: 'https://insightgainer.com/pricing' },
      //   { label: 'Blog', url: 'https://insightgainer.com/blog' },
      //   { label: 'Login', url: 'https://insightgainer.com/account/login' }
      // );
    } else {

      this.utils.logger.info(`InMemoryCacheService.getMainMenu() - logged in user flow`)

      const seoPlan = this.getClaimByType('plan', 'seoPlan');
      const localSeoPlan = this.getClaimByType('plan', 'localSeoPlan');
      const reviewPlan = this.getClaimByType('plan', 'reviewsPlan');
      const askKevinPlan = this.getClaimByType('plan', 'askKevinPlan');
      const brandMonitoringPlan = this.getClaimByType('plan', 'brandMonitoringPlan');
      const businessCardPlan = this.getClaimByType('plan', 'businessCardPlan');
      const permissionsService = this.injector.get(PermissionsService, null);
      const canViewGmbAudit = Boolean(localSeoPlan)
        && Boolean(permissionsService)
        && await permissionsService.canAsync('Org.GmbAudit.View');


      if (seoPlan) {
        this._mainMenu.push({ label: 'Dashboard', icon: 'fi fi-tr-chart-tree-map', routerLink: ['/dashboard'], visible: true, items: null });
        this._mainMenu.push({
          label: 'Keyword Research', icon: 'fi fi-tr-chart-mixed', routerLink: ['/seo/keyword-search'],
          items: [
            { label: 'Google Search', icon: this.getIcon('assets/icon/google.svg'), routerLink: ['/seo/keyword-search'] },
            { label: 'Bing Search', icon: this.getIcon('assets/icon/bing.svg'), routerLink: ['/seo/bing-search'] },
            { label: 'Amazon Search', icon: this.getIcon('assets/icon/amazon.svg'), routerLink: ['/seo/amazon-search'] },
          ]
        });
        this._mainMenu.push({
          label: 'Site Research', icon: 'fi fi-tr-chart-user', routerLink: [''],
          items: [
            { label: 'Overview', icon: 'fi fi-tr-file-chart-pie', routerLink: ['/seo/site-search/overview'] },
            {
              label: 'Backlinks', icon: 'fi fi-tr-link-horizontal', routerLink: [''],
              items: [
                { label: 'All', icon: 'fi fi-tr-link-horizontal', routerLink: ['/seo/site-search/backlinks'] },
                { label: 'New', icon: 'fi fi-tr-link-alt', routerLink: ['/seo/site-search/backlinks/new'] },
                { label: 'Lost', icon: 'fi fi-tr-link-slash', routerLink: ['/seo/site-search/backlinks/lost'] },
                { label: 'Broken', icon: 'fi fi-tr-link-slash-alt', routerLink: ['/seo/site-search/backlinks/broken'] },
              ]
            },
            { label: 'Top Pages', icon: 'fi fi-tr-arrow-square-up', routerLink: ['/seo/site-search/top-pages'] },
            { label: 'Referring Domains', icon: 'fi fi-tr-arrow-turn-down-left', routerLink: ['/seo/site-search/referring-domains'] },
            { label: 'Anchors', icon: 'fi fi-tr-anchor', routerLink: ['/seo/site-search/anchors'] }
          ]
        });
        this._mainMenu.push({
          label: 'Competitor Research', icon: 'fi fi-tr-chart-tree-map', routerLink: [''],
          items: [{
            label: 'Keyword Content Gap', icon: 'fi fi-tr-chart-tree-map',
            items: [
              {
                label: 'Google content gap', icon: this.getIcon('assets/icon/google.svg'), routerLink: ['/seo/competitor-research/keyword-content-gap']
              },
              {
                label: 'Bing content gap', icon: this.getIcon('assets/icon/bing.svg'), routerLink: ['/seo/competitor-research/bing-keyword-content-gap']
              },
              {
                label: 'Amazon content gap', icon: this.getIcon('assets/icon/amazon.svg'), routerLink: ['/seo/competitor-research/amazon-keyword-content-gap']
              },
            ]
          }, { label: 'Link Gap', icon: 'fi fi-tr-diagram-venn', routerLink: ['/seo/site-search/link-intersect'] }
          ],

        });

        // this._mainMenu.push({label: 'Leads', icon: 'pi pi-users', routerLink: ['/seo/leads']});
        this._mainMenu.push({ label: 'Rank Tracker', icon: 'fi fi-tr-chart-line-up', routerLink: ['/seo/rank-tracker'] });
        if (localSeoPlan) {
          this._mainMenu.push({ label: 'Local SEO', icon: 'pi pi-map-marker', routerLink: ['/seo/local-seo'] });
        }
        this._mainMenu.push({ label: 'Site Audit', icon: 'fi fi-tr-nfc-magnifying-glass', routerLink: ['/seo/audit'] });
        if (canViewGmbAudit) {
          this._mainMenu.push({ label: 'GMB Audit', icon: this.getParentIconSVG('assets/icon/gmb.svg'), routerLink: ['/seo/gmb-audit'] });
        }
        // this._mainMenu.push({ label: 'Google Traffic Report', icon: this.getParentIconSVG('assets/icon/google.svg'), routerLink: ['/seo/google-analytics-traffic-report']})
        this._mainMenu.push({
          label: "Paid Ads", icon: 'fi fi-tr-donate', routerLink: [''],
          items: [
            { label: 'Google Search', icon: this.getIcon('assets/icon/google.svg'), routerLink: ['/seo/google-paid-ads'] },
            { label: 'Bing Search', icon: this.getIcon('assets/icon/bing.svg'), routerLink: ['/seo/bing-paid-ads'] },
            { label: 'Amazon Search', icon: this.getIcon('assets/icon/amazon.svg'), routerLink: ['/seo/amazon-paid-ads'] },
          ]
        })
          this._mainMenu.push({
            label: 'Brand Monitoring', icon: this.getParentIconSVG('assets/icon/BrandMonitoring2.svg'), routerLink: [''],
            items: [
                {
                    label: 'Web Mentions', icon: this.getIcon('assets/icon/BrandMonitoring2.svg'), routerLink: ['/seo/brand-monitoring/web-monitoring']
                },
                {
                    label: 'Sentiment', icon: this.getIcon('assets/icon/BrandMonitoring2.svg'), routerLink: ['/seo/brand-monitoring/sentiment-overview']
                }
            ]
        });
        this._mainMenu.push({ label: 'Review Monitoring', icon: 'fi fi-tr-face-awesome', routerLink: ['/sentiment/reviews'] });
      }

      if (businessCardPlan) {
          this._mainMenu.push({ label: 'Digital Card', icon: 'pi pi-id-card', routerLink: ['/dbc'] });
        }

        if(askKevinPlan || businessCardPlan) {
            this._mainMenu.push({ label: 'Contacts', icon: 'pi pi-id-card', routerLink: ['/contacts']});
        }

      if (askKevinPlan) {
        this._mainMenu.push({
          label: 'Intelligence Suite', icon: 'fi fi-tr-brain-circuit', routerLink: [''],
          items: [
            // { label: 'Free Style', icon: 'fi fi-tr-brain-circuit', routerLink: ['/askkevin/answers'] },
            { label: 'Templates', icon: 'fi fi-tr-brain-circuit', routerLink: ['/askkevin/templates'] },
            //{ label: 'Keyword Analysis', icon: 'fi fi-tr-brain-circuit', routerLink: ['/askkevin/keyword-analysis'] },
            // { label: 'Article Writer', icon: 'fi fi-tr-brain-circuit', routerLink: ['/askkevin/workflow/create-topic'] },
            { label: 'Article Writer', icon: 'fi fi-tr-brain-circuit', routerLink: ['/askkevin/workflow/history'] },
            { label: 'Chatbot Management', icon: this.getIcon('assets/icon/chatbot.svg'), routerLink: ['/askkevin/chatbot'] }
          ]
        });
      }
    }
    this._mainMenuChanged.next(this._mainMenu);
  }

  public async getClaimsAndUpdateMenu(): Promise<void> {
    await this.getClaims();
    this.getMainMenu();
  }

  public async getProject(projectId: string, userId: string, handleError: boolean = false): Promise<any> {
    this.utils.logger.info(`InMemoryCacheService.getProject()`);
    const response = { data: null, error: null };

    if (!projectId || !userId) {
      this.utils.logger.info(`InMemoryCacheService.getProject() - no projectId or userId, returning null`);
      return null;
    }

    try {
      response.data = this._selectedProject = await firstValueFrom(this.core.get(`${environment.identityUrl}/project/${projectId}/user/${userId}`));
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async getProjectByOrg(projectId: string, organizationId: string, handleError: boolean = false): Promise<any> {
    this.utils.logger.info(`InMemoryCacheService.getProjectByOrg()`);
    const response = { data: null, error: null };

    if (!projectId || !organizationId) {
      this.utils.logger.info(`InMemoryCacheService.getProjectByOrg() - no projectId or organizationId, returning null`);
      return null;
    }

    try {
      response.data = this._selectedProject = await firstValueFrom(this.core.get(`${environment.identityUrl}/project/${projectId}/org/${organizationId}`));
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  // public getProperty(name: string, type: string) {
  //   return this.userToken?.organization?.orgProps?.find(p => p.name.toLowerCase() === name.toLowerCase()
  //   && p.type.toLowerCase() === type.toLowerCase());
  // }

  // public getPropertyByType(type: string) {
  //   return (this.userToken?.organization?.orgProps?.filter(p => p.type.toLowerCase() === type.toLowerCase()));
  // }

  // get userToken(): UserToken {
  //   return this._token;
  // }

  // set userToken(value: UserToken) {
  //   this._token = value;
  // }

  get claims(): any {
    return this._claims;
  }

  get userId(): string {
    const userId = this.auth?.user?.user?.id;
    if (!userId) {
      this.logout();
      return null;
    }
    return userId;
  }

  get user(): User {
    const user = this.auth?.user?.user;
    return user;
  }

  set user(value: User) {
    var oldUser = JSON.parse(localStorage.getItem('currentUser'));
    this.auth.user.user = oldUser.user = value;
    localStorage.setItem('currentUser', JSON.stringify(oldUser));
  }

  get organization(): Organization {
    const organization = this.auth?.organization;
    return organization;
  }

  set organization(value: Organization) {
    this.auth.organization = value;
  }


  get currentProject(): CurrentProject {
    return localStorage.getItem('currentProject') ? JSON.parse(localStorage.getItem('currentProject')) : null;
  }

  set currentProject(project: Project) {
    if (!project || !project.id) {
      throw new Error('Project cannot be null');
    }

    const resolvedOwnerUserId = project.userId || project.ownerUserId || this.userId;
    const currentProject: CurrentProject = {
      id: project.id,
      name: project.name,
      domain: project.domain,
      userId: resolvedOwnerUserId,
      ownerUserId: resolvedOwnerUserId
    };

    localStorage.setItem('currentProject', JSON.stringify(currentProject));
  }

  get seoLanguagesFlat(): SeoLanguageFlat[] {
    return this._seoLanguagesFlat;
  }

  get seoLanguages(): SeoLanguage[] {
    return this._seoLanguages;
  }

  get amazonLocations(): any[] {
    return this._amazonMerchantLocations;
  }

  get amazonLanguages(): any[] {
    return this._amazonMerchantLanguages;
  }

  get tripAdvisorLocations(): any[] {
    return this._tripAdvisorLocations;
  }

  get tripAdvisorLanguages(): any[] {
    return this._tripAdvisorLanguages;
  }

  get media(): Media[] {
    return this._media;
  }

  set media(value: Media[]) {
    this._media = value;
  }

  get auth(): AuthService {
    return this.authService;
  }

  get plans(): Plan[] {
    return this._plans;
  }

  set plans(value: Plan[]) {
    this._plans = value;
  }

  get selectedPlan(): Plan {
    return this._selectedPlan;
  }

  set selectedPlan(value: Plan) {
    this._selectedPlan = value;
  }

  get billingDetails(): BillingDetails {
    return this._billingDetails;
  }

  set billingDetails(value: BillingDetails) {
    this._billingDetails = value;
  }

  get billingProducts(): BillingProduct[] {
    return this._billingProducts;
  }

  set billingProducts(value: BillingProduct[]) {
    this._billingProducts = value;
  }

  get invoice(): Invoice[] {
    return this._invoice;
  }

  set invoice(value: Invoice[]) {
    this._invoice = value;
  }

  get showErrorModal(): boolean {
    return this._showErrorModal;
  }

  set showErrorModal(value: boolean) {
    this._showErrorModal = value;
  }

  get states() {
    return this.constantService.states;
  }

  get payBtnDisabled() {
    return this._payBtnDisabled;
  }

  set payBtnDisabled(value: boolean) {
    this._payBtnDisabled = value;
  }

  get isLoading(): BehaviorSubject<boolean> {
    return this._loading;
  }

  set loading(value: boolean) {
    if (this._loaderCount < 0) {
      this._loaderCount = 0;
    }

    if (value) {
      this._loaderCount++;
    } else {
      this._loaderCount--;
    }

    setTimeout(() => {
      this._loading.next(this._loaderCount > 0);
    }, 0);
  }

  get mainMenu(): BehaviorSubject<any> {
    return this._mainMenuChanged;
  }

  get pagination(): Pagination {
    return this._pagination;
  }

  get menuMode(): BehaviorSubject<any> {
    return this._menuModeChanged;
  }

  set updateMenuMode(value: string) {
    this._menuModeChanged.next(value);
  }

  set pagination(value: Pagination) {
    this._pagination = value;
  }

  get searchStatus() {
    return this.constantService.searchStatus;
  }

  get stripe() {
    return this._stripe;
  }

  get seoLimits(): SeoLimits {
    return this._seoLimits;
  }

  get selectedProject(): any {
    return this._selectedProject;
  }

  set selectedProject(value: any) {
    this._selectedProject = value;
  }

}
